/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnInit, type SimpleChanges, Component, Injector, Input } from '@angular/core';
import { type ColumnDef, type Rest, type TableConfig, ButtonService, TableAbstract, UtilService } from '@dm/core';
import { type Button } from '@dm/core/types';
import { type CIFSShare, type ExtensionFiltering, type NFSShare } from 'app/common/types/mo';
import { SWITCH } from 'app/config';

import { AddExtensionFilteringComponent } from './add-extension-filtering/add-extension-filtering.component';
import { DeleteExtensionFilteringComponent } from './delete-extension-filtering/delete-extension-filtering.component';
import { ExtensionFilteringStrService, ShareMoTypeE } from './extension-filtering-str.service';
import { ExtensionFilteringService } from './extenxion-filtering.service';

@Component({
    selector: 'app-extension-filtering',
    templateUrl: './extension-filtering.component.html',
    styleUrls: ['./extension-filtering.component.scss'],
})
export class ExtensionFilteringComponent extends TableAbstract<ExtensionFiltering> implements OnInit, OnChanges {
    @Input() share: CIFSShare | NFSShare;
    @Input() shareType: ShareMoTypeE;

    i18n = this.str.i18n;

    tableRest: Rest;
    tableConfig: TableConfig;
    tableColumnDefs: ColumnDef[];
    selection: any[] = [];
    loading = false;
    isSimpleMode = SWITCH.IS_SIMPLE_MODE;

    addBtn: Button;
    modifyBtn: Button;
    removeBtn: Button;

    constructor(
        public str: ExtensionFilteringStrService,
        private service: ExtensionFilteringService,
        private buttonService: ButtonService,
        private utilService: UtilService,
        injector: Injector,
    ) {
        super(injector);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((!changes.share.previousValue && changes.share.currentValue) ||
            (changes.share.previousValue && changes.share.currentValue &&
                changes.share.currentValue.ID !== changes.share.previousValue.ID)) {
            this.tableRest = this.service.getBatchRest(changes.share.currentValue.ID, this.shareType, changes.share.currentValue.vstoreId);
        }
    }

    initBtns(): void {
        this.addBtn = this.buttonService.create({
            id: 'add-extension-filtering',
            label: this.i18n.addBtn,
            theme: 'primary',
            permission: ['share_W'],
            command: () => {
                this.add();
            },
        });
        this.modifyBtn = this.buttonService.create({
            id: 'modify-extension-filtering',
            label: this.i18n.modifyBtn,
            permission: ['share_W'],
            command: ({ data }) => {
                this.modify(data);
            },
        });
        this.removeBtn = this.buttonService.create({
            id: 'remove-extension-filtering',
            label: this.i18n.removeBtn,
            permission: ['share_W'],
            disabledCheck: (data) => !data && this.selection.length === 0,
            command: ({ data }) => {
                this.remove(data ? [data] : this.selection);
            },
        });
    }

    initTable(): void {
        this.tableConfig = {
            dataKey: 'ID',
            selectionMode: 'checkbox',
            pollingPeriod: 15,
            menuItems: [
                this.modifyBtn,
                this.removeBtn,
            ],
        };

        this.tableColumnDefs = [
            {
                field: 'FILEEXTENSIONNAME',
                header: this.i18n.fileNameExtension,
                filterType: 'search',
                filterTrim: false,
            },
            {
                field: 'ID',
                header: this.i18n.id,
                hidden: true,
                columnFilter: true,
            },
            {
                field: 'RULETYPE',
                header: this.i18n.ruleType,
                filterType: 'dropdown',
                options: this.str.ruleTypeOpt,
                render: (value) => this.utilService.getLabelFromOptions(this.str.ruleTypeOpt, value),
                columnFilter: true,
            },
            {
                field: 'FILEOPERATIONS',
                header: this.i18n.operationType,
                columnFilter: true,
                render: (value) => this.str.fileOperationRender(value, this.shareType),
            },
        ];
    }

    add(): void {
        this.doAction(AddExtensionFilteringComponent, {
            share: this.share,
            shareType: this.shareType,
        });
    }

    modify(data: ExtensionFiltering): void {
        this.doAction(AddExtensionFilteringComponent, {
            share: this.share,
            shareType: this.shareType,
            data,
        });
    }

    remove(dataList: ExtensionFiltering[]): void {
        this.doAction(DeleteExtensionFilteringComponent, {
            dataList,
            share: this.share,
        });
    }
}
